import type { TabItem } from '@/components/layout/Tabs';
import type { TimelineStep } from '@/components/data/Timeline';
import {
  CHANNEL_TABS,
  CRM_TABS,
  INBOX_TABS,
  INSIGHTS_TABS,
  SETTINGS_TABS,
} from '@/components/navigation/SubNavTabs';
import { simpleTenantNavItems, tenantNavItems } from '@/apps/tenant/nav.config';
import type { NavItem } from '@/types/nav';
import type { TenantViewMode } from '@/types/view-mode';

const CRM_SIMPLE_TAB_IDS = new Set(['follow-ups', 'appointments', 'contacts', 'leads']);
const CHANNEL_SIMPLE_TAB_IDS = new Set(['web', 'whatsapp', 'test']);
const SETTINGS_SIMPLE_TAB_IDS = new Set(['profile', 'notifications', 'account']);
const INBOX_SIMPLE_TAB_IDS = new Set(['conversations']);

const CRM_SIMPLE_LABELS: Record<string, string> = {
  'follow-ups': 'Waiting for reply',
  appointments: 'Bookings',
  leads: 'New enquiries',
  contacts: 'All customers',
};

function relabelTabs(tabs: TabItem[], labels: Record<string, string>): TabItem[] {
  return tabs.map((tab) => ({
    ...tab,
    label: labels[tab.id] ?? tab.label,
  }));
}

export function getTenantNavItems(mode: TenantViewMode): NavItem[] {
  return mode === 'expert' ? tenantNavItems : simpleTenantNavItems;
}

export function getCrmTabs(mode: TenantViewMode): TabItem[] {
  if (mode === 'expert') {
    return CRM_TABS;
  }
  return relabelTabs(
    CRM_TABS.filter((tab) => CRM_SIMPLE_TAB_IDS.has(tab.id)),
    CRM_SIMPLE_LABELS,
  );
}

export function getInboxTabs(mode: TenantViewMode): TabItem[] {
  if (mode === 'expert') {
    return INBOX_TABS;
  }
  return INBOX_TABS.filter((tab) => INBOX_SIMPLE_TAB_IDS.has(tab.id));
}

export function getChannelTabs(mode: TenantViewMode): TabItem[] {
  if (mode === 'expert') {
    return CHANNEL_TABS;
  }
  return CHANNEL_TABS.filter((tab) => CHANNEL_SIMPLE_TAB_IDS.has(tab.id));
}

export function getSettingsTabs(mode: TenantViewMode): TabItem[] {
  if (mode === 'expert') {
    return SETTINGS_TABS;
  }
  return SETTINGS_TABS.filter((tab) => SETTINGS_SIMPLE_TAB_IDS.has(tab.id));
}

export function getInsightsTabs(mode: TenantViewMode): TabItem[] {
  if (mode === 'expert') {
    return INSIGHTS_TABS;
  }
  return [];
}

/** Redirect simple-mode users away from expert-only routes. */
export function simpleModeRedirectPath(pathname: string): string | null {
  if (pathname.includes('/crm/deals')) return '/tenant/crm/follow-ups';
  if (pathname.includes('/crm/templates')) return '/tenant/crm/follow-ups';
  if (pathname.includes('/inbox/leads')) return '/tenant/inbox/conversations';
  if (pathname.includes('/insights/')) return '/tenant/home';
  // Knowledge is read-only in Simple — crawl/upload stay admin-managed on the page
  if (pathname.includes('/channels/voice')) {
    return '/tenant/home';
  }
  if (pathname.includes('/settings/ai-rag') || pathname.includes('/settings/api-keys')) {
    return '/tenant/settings/profile';
  }
  if (pathname.includes('/simulators/whatsapp')) return '/tenant/channels/test?tab=whatsapp';
  if (pathname.includes('/simulators/')) return '/tenant/home';
  if (pathname.includes('/integrations') || pathname.includes('/automation')) {
    return '/tenant/home';
  }
  return null;
}

export function isExpertOnlyRoute(pathname: string): boolean {
  return simpleModeRedirectPath(pathname) !== null;
}

const SIMPLE_CHECKLIST_IDS = new Set(['profile', 'calendar', 'booking']);

/** Checklist steps a non-technical owner can act on without Expert view. */
export function getSimpleChecklistSteps(steps: TimelineStep[]): TimelineStep[] {
  return steps
    .filter((step) => SIMPLE_CHECKLIST_IDS.has(step.id))
    .map((step) => {
      if (step.id === 'booking') {
        return {
          ...step,
          label: 'Receive your first booking',
          href: '/tenant/crm/appointments',
        };
      }
      return step;
    });
}

export function isSimpleSetupComplete(steps: TimelineStep[]): boolean {
  const simple = getSimpleChecklistSteps(steps);
  return simple.length > 0 && simple.every((step) => step.status === 'complete');
}
